import { styled } from "@linaria/react"
import { useNavigate, useRouter } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"

import { Vertical } from "@/components/Flex.ts"
import { PromptInput } from "@/components/PromptInput/PromptInput.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { useModel } from "@/hooks/useModel.ts"
import { useInvalidateQueries, useOrgContext } from "@/hooks/useRootContext.ts"
import { startViewTransition } from "@/lib/dom.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { NewProjectFormModel } from "@/routes/projects/NewProjectFormModel.ts"
import { TaskNewSceneDevPanel } from "@/routes/tasks/TaskNewScene/TaskNewScene.tsx"
import { smallerFontSize } from "@/styles/textStyles.ts"

import type { AiAgentTask } from "@/lib/client/generated/TypeDefinitions.ts"


export const NewProjectForm = observer(() => {
  const context = useOrgContext()
  const invalidateQueries = useInvalidateQueries()
  const devMode = useDevMode()

  const navigate = useNavigate()
  const router = useRouter()

  const model = useModel(() => {
    const onComplete = (task: AiAgentTask) => {
      const redirectLocation = Navigator.task(context.orgId, task.projectKey, task.id, {})

      startViewTransition(() => navigate(redirectLocation))
    }

    return new NewProjectFormModel(context, invalidateQueries, router, onComplete)
  })

  const validationError = model.inputModel.validationError.get()

  return (
    <FormContainer>
      <PromptInput model={model.inputModel} singleLine />

      {validationError && (
        <ValidationError>
          {validationError}
        </ValidationError>
      )}

      {devMode && (
        <TaskNewSceneDevPanel model={model.taskSettings} />
      )}
    </FormContainer>
  )
})

const FormContainer = styled(Vertical)`
  gap: 16px;
  width: 100%;
`

const ValidationError = styled.span`
  ${smallerFontSize};

  color: var(--error-color)
`